import { useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface DialogueState {
  text: string;
  speaker?: string;
  variant: 'dialogue' | 'trap' | 'loading' | 'quest';
}

export interface DialogueOverlayProps {
  text: string;
  speaker?: string;
  variant?: 'dialogue' | 'trap' | 'loading' | 'quest';
  onDismiss: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function DialogueOverlay({ text, speaker, variant = 'dialogue', onDismiss }: DialogueOverlayProps) {
  // Dismiss on keypress (not when loading)
  useEffect(() => {
    if (variant === 'loading') return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onDismiss();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onDismiss, variant]);

  const isTrap = variant === 'trap';
  const isQuest = variant === 'quest';
  const isLoading = variant === 'loading';

  return (
    <div
      className="absolute inset-0 z-40 flex items-end justify-center pb-8 pointer-events-none"
    >
      <div
        className={cn(
          'pointer-events-auto max-w-lg w-full mx-4 rounded-lg border shadow-2xl shadow-black/60',
          isLoading ? 'bg-neutral-900/95 border-purple-500/30' :
          isTrap ? 'bg-red-950/95 border-red-500/30' :
          isQuest ? 'bg-amber-950/95 border-amber-500/30' :
          'bg-neutral-900/95 border-neutral-700',
          !isLoading && 'cursor-pointer',
        )}
        onClick={isLoading ? undefined : onDismiss}
      >
        <div className="px-5 py-4">
          {isLoading && (
            <div className="flex items-center gap-2 text-purple-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-xs font-medium uppercase tracking-wide">{text}</span>
            </div>
          )}
          {!isLoading && (
            <>
              {speaker && (
                <div className={cn(
                  'text-xs font-medium uppercase tracking-wide mb-1.5',
                  isTrap ? 'text-red-400' : isQuest ? 'text-amber-300' : 'text-amber-400',
                )}>
                  {speaker}
                </div>
              )}
              {isTrap && !speaker && (
                <div className="text-xs font-medium uppercase tracking-wide mb-1.5 text-red-400">
                  Trap!
                </div>
              )}
              <p className="text-sm leading-relaxed text-neutral-200">{text}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-neutral-500">Click or press Space to dismiss</span>
                <X className="w-3.5 h-3.5 text-neutral-500" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
